import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { SKYDIO_STANDBY_WINDOW_MS, SupportedRegions } from "@nova/constants"
import useSWR, { mutate } from "swr"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectLiveVideoFlightsMap } from "features/entity-caching/flights/selectors"

export const invalidatePendingLiveStreams = () =>
    mutate((key) => Array.isArray(key) && key[0] === "pending-live-streams")

const getPendingLiveStreams = (orgId: string, projectId: string) =>
    fetchApiGateway("get /org/:orgId/project/:projectId/live-stream/pending", {
        params: { orgId, projectId },
    })

const msUntilNextExpiry = (pendingLiveStreams?: { createdAt: string }[]) => {
    const nextExpiry = Math.min(
        ...(pendingLiveStreams ?? []).map(
            ({ createdAt }) => Date.parse(createdAt) + SKYDIO_STANDBY_WINDOW_MS
        )
    )
    return Number.isFinite(nextExpiry) ? Math.max(nextExpiry - Date.now(), 1) : 0
}

const EMPTY_DATA_ARRAY = [] as const
export const usePendingLiveStreams = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const projectId = useAppSelector(selectActivatedFireId)
    const flightsById = useAppSelector(selectLiveVideoFlightsMap)

    const { data } = useSWR(
        orgId && projectId ? ["pending-live-streams", orgId, projectId] : null,
        () => getPendingLiveStreams(orgId!, projectId!),
        { refreshInterval: msUntilNextExpiry }
    )

    return (data ?? EMPTY_DATA_ARRAY)
        .filter(({ layerId }) => !flightsById[layerId])
        .map((pending) => ({ ...pending, name: pending.name ?? "Live Stream" }))
}

export const usePendingLiveStream = (layerId: string) =>
    usePendingLiveStreams().find((pending) => pending.layerId === layerId)

export const useDeletePendingLiveStream = () => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const projectId = useAppSelector(selectActivatedFireId)

    return async (layerId: string) => {
        if (!orgId || !projectId) return
        await fetchApiGateway(
            "delete /org/:orgId/project/:projectId/live-stream/pending/:layerId",
            {
                params: { orgId, projectId, layerId },
                region: SupportedRegions.Canada, // needs to be canada-only to reach canadian redis
            }
        )
    }
}
